import { useEffect, useState } from 'react';
import type { ActivitySubjectCard } from '@shared/types/activity-feed';
import type { AppWorkSummary } from '@shared/types/thread-work';
import { listActivitySubjects } from './activity-feed';
import { listAppWork } from './app-work';
import { supabase } from './supabase';

/**
 * Mon travail at team and company scope (21/10/2026), for managers: the team's
 * week and the review of the apps. Members' files are read with their own read
 * models (activity-feed `memberUserId`); the server refuses anyone else.
 */
export type WorkScope = 'me' | 'team' | 'company';

export const scopeOf = (value: string | null): WorkScope => (value === 'equipe' ? 'team' : value === 'entreprise' ? 'company' : 'me');

export const useIsTenantManager = () => {
  const [manager, setManager] = useState(false);
  useEffect(() => {
    void (async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;
      const { data } = await supabase.from('tenant_members').select('tenant_role').eq('user_id', user.id).maybeSingle();
      setManager((data as { tenant_role?: string } | null)?.tenant_role === 'manager');
    })();
  }, []);
  return manager;
};

export interface TeamMemberWeek {
  userId: string;
  name: string;
  /** Launched, set aside with a motive, rituals done, relays ended, since Monday. */
  done: number;
}

export const loadTeamWeek = async (since: Date): Promise<TeamMemberWeek[]> => {
  const { data, error } = await supabase.rpc('read_team_week_j7', { p_since: since.toISOString() });
  if (error) throw new Error('La semaine de l’équipe n’est pas accessible pour le moment.');
  return ((data ?? []) as Array<{ user_id: string; display_name: string | null; done: number }>).map((row) => ({
    userId: row.user_id, name: row.display_name?.trim() || 'Membre sans nom affiché', done: row.done,
  }));
};

export interface MemberFile {
  subjects: ActivitySubjectCard[];
  apps: AppWorkSummary[];
}

export const loadMemberFile = async (memberUserId: string): Promise<MemberFile> => {
  const [subjects, apps] = await Promise.all([listActivitySubjects(memberUserId), listAppWork(memberUserId)]);
  return { subjects: subjects.subjects, apps };
};

export interface PlayReview {
  toolAppId: string;
  appKey: string;
  title: string;
  flowId: string | null;
  created: number;
  statuses: Array<{ key: string; label: string; count: number }>;
  launched: number;
  setAside: Record<string, number>;
}

export const loadPlaysReview = async (from: Date, to: Date): Promise<PlayReview[]> => {
  const { data, error } = await supabase.rpc('read_plays_review_j7', { p_from: from.toISOString(), p_to: to.toISOString() });
  if (error) throw new Error('La revue des coups n’est pas accessible pour le moment.');
  return ((data ?? []) as Array<{
    tool_app_id: string; app_key: string; app_title: string; flow_id: string | null; created_count: number;
    statuses: PlayReview['statuses']; launched: number; set_aside: Record<string, number>;
  }>).map((row) => ({
    toolAppId: row.tool_app_id, appKey: row.app_key, title: row.app_title, flowId: row.flow_id,
    created: row.created_count, statuses: row.statuses, launched: row.launched, setAside: row.set_aside,
  }));
};

/** Monday 00:00 of this week, in the person's calendar. */
export const weekStart = (now: Date): Date => {
  const start = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  start.setDate(start.getDate() - ((start.getDay() + 6) % 7));
  return start;
};
